import { type DesignNode, isPointInShape } from '@repo/api-contracts';
import {
  type CreateContext,
  hitTestLayers,
  type MeasureText,
  type PhotoDimensions,
  type RenderMaterials,
  renderComponent,
} from '@repo/design-renderer';
import type { Raster } from '@repo/warp-engine';
import {
  Fragment,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  type BoxNode,
  clientToComponentMm,
  dragMove,
  dragResize,
  dragRotate,
  dragTextWidth,
  type FlatFitMode,
  type FlatView,
  findNode,
  fitSpaceView,
  type HandleKind,
  hitHandle,
  rasterPxPerMmOf,
  type SpaceFit,
  type SurfaceSlot,
  surfaceBoxOf,
  surfaceViewIn,
  unionRectOf,
} from '@/features/design-editor';
import { drawRasterToCanvas } from '@/shared/lib';
import { cn } from '@/shared/ui';
import { AreaShapeOutline } from './area-shape-outline';
import { SelectionOverlay } from './selection-overlay';
import { TextEditHint } from './text-edit-hint';

/** 캔버스에 올리는 편집 표면 하나 — 면에 배치된 인쇄 영역과 그 레이어. */
export interface EditSurface extends SurfaceSlot {
  readonly layers: readonly DesignNode[];
}

interface FlatEditCanvasProps {
  /** 이 면의 인쇄 영역 전부 — 한 화면에 같이 보이고 어느 것이든 바로 편집된다. */
  readonly surfaces: readonly EditSurface[];
  /** 공유 공간 크기 — 사진이 있으면 사진 px, 없으면 가상 mm 공간. 자리(rect)는 이 공간 위 좌표다. */
  readonly space: PhotoDimensions;
  /** 지금 편집 중인(텍스트·이미지가 들어가는) 인쇄 영역. 다른 영역을 누르면 onActivate 로 바뀐다. */
  readonly activeComponentId: string;
  readonly onActivate: (componentId: string) => void;
  readonly selectedNodeId: string | null;
  readonly materials: RenderMaterials;
  /** 지금 편집 중인 영역의 안내선 — 선택 중일 때 그 영역 위에만 겹친다. */
  readonly guide: Raster | null;
  readonly createContext: CreateContext;
  readonly measure: MeasureText;
  readonly onSelect: (nodeId: string | null) => void;
  readonly onChangeNode: (node: BoxNode) => void;
  readonly onNudge: (dxMm: number, dyMm: number) => void;
  readonly onDeleteSelected: () => void;
  /** 있으면 목업 사진을 공간 전체에 깔고 그 위에 편집 표면들을 겹친다. 없으면 흰 인쇄 영역들만 보인다. */
  readonly backdrop?: { readonly url: string; readonly approximate: boolean } | null;
  /** 텍스트 레이어를 더블클릭했을 때 — 호출자가 내용 칸으로 포커스를 옮긴다. */
  readonly onEditText?: (nodeId: string) => void;
  /** 곡면 상품의 실제 합성(사진 전체 크기) — 있고 끌고 있지 않으면 평면 겹침 대신 이것을 보인다. */
  readonly warped?: ImageBitmap | null;
  /** 곡면 합성 위 클릭(사진 원본 px) → 역워프로 고른 레이어 id. 영역 전환은 호출자가 한다. */
  readonly onWarpedHit?: (photoPx: { x: number; y: number }) => string | null;
  /** 목업 사진을 못 불러왔을 때(서명 URL 만료 등) — 호출자가 새 URL 을 받는다. */
  readonly onBackdropError?: () => void;
  /**
   * 보기 모드 — photo(전체 보기, 자동 맞춤)·area(영역 확대: 인쇄 영역들을 채운 배율에서 시작해 휠로 확대·축소하고
   * 빈 곳을 끌어 옮길 수 있다). 전체 보기에서 휠을 굴리면 영역 확대로 넘어간다(onFitModeChange).
   */
  readonly fitMode?: FlatFitMode;
  readonly onFitModeChange?: (mode: FlatFitMode) => void;
  /** 스펙 대역용 — 기본은 렌더러·캔버스 그리기. */
  readonly renderComponentImpl?: typeof renderComponent;
  readonly drawImpl?: (canvas: HTMLCanvasElement, raster: Raster) => void;
}

interface Drag {
  readonly kind: HandleKind;
  readonly original: BoxNode;
  readonly componentId: string;
  readonly startMm: { xMm: number; yMm: number };
}

/** 빈 곳 끌기 — 움직이면 화면 이동, 안 움직이고 떼면 누른 자리의 클릭(선택 해제·영역 전환)으로 본다. */
interface Pan {
  readonly pointerId: number;
  readonly startX: number;
  readonly startY: number;
  readonly left: number;
  readonly top: number;
  readonly picked: Picked;
  readonly moved: boolean;
}

interface SelectedBox {
  readonly node: BoxNode;
  readonly componentId: string;
}

interface Picked {
  readonly nodeId: string | null;
  /** 누른 자리의 영역. 곡면 역워프 히트는 영역을 모른다(호출자가 바꾼다). */
  readonly componentId: string | null;
}

const PADDING_PX = 24;
/** 영역 확대에서 허용하는 배율 — 전체 보기 배율의 몇 배까지. */
const MIN_ZOOM_OF_FIT = 0.5;
const MAX_ZOOM_OF_FIT = 8;
/** 이만큼 안 움직이고 떼면 끌기가 아니라 클릭이다. */
const PAN_THRESHOLD_PX = 3;

/** 표면 전부를 문서 모양으로 — findNode 가 노드와 그 영역을 같이 찾는다. */
const documentOf = (surfaces: readonly EditSurface[]) => ({
  productId: '',
  colorOptionId: '',
  components: surfaces.map((surface) => ({
    componentId: surface.component.id,
    layers: [...surface.layers],
  })),
});

const selectedBoxOf = (surfaces: readonly EditSurface[], id: string | null): SelectedBox | null => {
  if (id === null) return null;
  const found = findNode(documentOf(surfaces), id);
  return found !== null && found.node.type !== 'group'
    ? { node: found.node, componentId: found.componentId }
    : null;
};

const insideComponent = (surface: EditSurface, mm: { xMm: number; yMm: number }) =>
  isPointInShape(surface.component, mm);

/**
 * 평면 편집 캔버스(U6 자체 Canvas 2D). 한 면의 인쇄 영역 전부를 공유 공간(목업 사진) 위 제자리에 겹쳐 그리고,
 * 어느 영역의 레이어든 바로 누르면 선택된다 — 누른 영역이 편집 중인 영역이 된다.
 * 포인터: 핸들 → 크기/회전/폭, 노드 → 이동, 빈 곳 → 선택 해제(다른 영역의 빈 곳이면 그 영역으로 전환), 텍스트 더블클릭 → 글자 고치기.
 * 키보드: 화살표 1mm(Shift 10), Delete, Esc(선택 해제).
 */
export const FlatEditCanvas = ({
  surfaces,
  space,
  activeComponentId,
  onActivate,
  selectedNodeId,
  materials,
  guide,
  createContext,
  measure,
  onSelect,
  onChangeNode,
  onNudge,
  onDeleteSelected,
  backdrop = null,
  onEditText,
  onBackdropError,
  warped = null,
  onWarpedHit,
  fitMode = 'photo',
  onFitModeChange,
  renderComponentImpl = renderComponent,
  drawImpl = drawRasterToCanvas,
}: FlatEditCanvasProps): ReactElement => {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRefs = useRef(new Map<string, HTMLCanvasElement>());
  const guideRef = useRef<HTMLCanvasElement>(null);
  const warpedRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ width: 400, height: 300 });
  const [drag, setDrag] = useState<Drag | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [live, setLive] = useState<BoxNode | null>(null);

  // 사진이 없으면 가상 공간이 곧 영역들의 외접 사각형이라 전체 보기로 꽉 채운다.
  const focus = unionRectOf(surfaces.map((surface) => surface.rect)) ?? {
    x: 0,
    y: 0,
    width: space.widthPx,
    height: space.heightPx,
  };
  // 영역 확대 모드의 사용자 배율·위치. null 이면 그 모드의 자동 맞춤을 쓴다(모드에 처음 들어왔을 때).
  const [free, setFree] = useState<SpaceFit | null>(null);
  const [pan, setPan] = useState<Pan | null>(null);
  const wholeFit = fitSpaceView(size, space, focus, PADDING_PX, 'photo');
  const autoFit =
    fitMode === 'area' ? fitSpaceView(size, space, focus, PADDING_PX, 'area') : wholeFit;
  const fit = fitMode === 'area' && free !== null ? free : autoFit;
  const photoBox = {
    left: fit.left,
    top: fit.top,
    width: space.widthPx * fit.k,
    height: space.heightPx * fit.k,
  };
  const fallbackView: FlatView = { pxPerMmX: 1, pxPerMmY: 1, offsetX: fit.left, offsetY: fit.top };
  const viewOf = (componentId: string): FlatView => {
    const surface = surfaces.find((candidate) => candidate.component.id === componentId);
    return surface === undefined
      ? fallbackView
      : surfaceViewIn(fit, surface.rect, surface.component);
  };
  const surfaceOf = (componentId: string): EditSurface | undefined =>
    surfaces.find((surface) => surface.component.id === componentId);

  const warpedShown = warped !== null && drag === null && backdrop !== null;
  const stored = selectedBoxOf(surfaces, selectedNodeId);
  const selected: SelectedBox | null =
    live !== null && drag !== null ? { node: live, componentId: drag.componentId } : stored;
  const selectedView = selected === null ? null : viewOf(selected.componentId);

  useEffect(() => {
    const host = hostRef.current;
    if (host === null || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: 배치는 fit(배율·위치)과 surfaces 로 결정되므로 그 둘로 다시 그린다.
  useEffect(() => {
    const dpr = window.devicePixelRatio || 1;
    for (const surface of surfaces) {
      const canvas = canvasRefs.current.get(surface.component.id);
      if (canvas === undefined) continue;
      const view = surfaceViewIn(fit, surface.rect, surface.component);
      try {
        // 곡면 합성이 보이는 동안은 평면 겹침을 비워 인쇄 영역 점선만 남긴다.
        const shown = warpedShown
          ? []
          : live === null
            ? surface.layers
            : surface.layers.map((node) => (node.id === live.id ? live : node));
        const raster = renderComponentImpl({
          component: surface.component,
          layers: shown,
          materials,
          // 렌더러처럼 큰 쪽 축척으로 그리고, 캔버스 CSS 크기(축마다 따로)가 결과물처럼 늘인다.
          pxPerMm: rasterPxPerMmOf(view) * dpr,
          createContext,
          measure,
        });
        drawImpl(canvas, raster);
      } catch {
        // 재료가 아직 없으면 다음 렌더에서 그린다.
      }
    }
    if (guideRef.current !== null && guide !== null && selected !== null) {
      try {
        drawImpl(guideRef.current, guide);
      } catch {
        // 안내선 래스터가 아직 없으면 다음 렌더에서 그린다.
      }
    }
  }, [
    surfaces,
    live,
    materials,
    fit.k,
    fit.left,
    fit.top,
    createContext,
    measure,
    guide,
    selected,
    renderComponentImpl,
    drawImpl,
    warpedShown,
  ]);

  useEffect(() => {
    if (fitMode !== 'area') setFree(null);
  }, [fitMode]);

  /** 화면 한 점을 고정한 채 배율을 바꾼다 — 전체 보기 배율의 0.5~8배로 묶는다. */
  const zoomAt = (base: SpaceFit, factor: number, cx: number, cy: number): SpaceFit => {
    const k = Math.min(
      wholeFit.k * MAX_ZOOM_OF_FIT,
      Math.max(wholeFit.k * MIN_ZOOM_OF_FIT, base.k * factor),
    );
    const ratio = k / base.k;
    return { k, left: cx - (cx - base.left) * ratio, top: cy - (cy - base.top) * ratio };
  };

  // React 의 onWheel 은 passive 라 페이지 스크롤을 못 막는다 — 직접 붙인다. 최신 fit·모드를 클로저로 읽도록 매 렌더 다시 단다.
  useEffect(() => {
    const host = hostRef.current;
    if (host === null) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const rect = host.getBoundingClientRect();
      const factor = Math.exp(-event.deltaY * (event.ctrlKey ? 0.01 : 0.0015));
      const next = zoomAt(fit, factor, event.clientX - rect.left, event.clientY - rect.top);
      setFree(next);
      if (fitMode !== 'area') onFitModeChange?.('area');
    };
    host.addEventListener('wheel', onWheel, { passive: false });
    return () => host.removeEventListener('wheel', onWheel);
  });

  useEffect(() => {
    const canvas = warpedRef.current;
    if (canvas === null || warped === null) return;
    canvas.width = warped.width;
    canvas.height = warped.height;
    canvas.getContext('2d')?.drawImage(warped, 0, 0);
  }, [warped]);

  const hostRect = () => hostRef.current?.getBoundingClientRect() ?? { left: 0, top: 0 };

  const mmIn = (view: FlatView, event: { clientX: number; clientY: number }) =>
    clientToComponentMm(view, hostRect(), { x: event.clientX, y: event.clientY });

  /**
   * 누른 자리를 고른다. 곡면 합성이 보이면 사진 원본 px 로 바꿔 역워프 히트. 아니면 편집 중인 영역부터 차례로
   * 레이어 히트를 보고, 레이어가 없으면 누른 자리가 든 영역(빈 곳)을 알린다.
   */
  const pickAt = (event: { clientX: number; clientY: number }): Picked => {
    if (warpedShown && onWarpedHit !== undefined) {
      const rect = hostRect();
      const k = space.widthPx / photoBox.width;
      return {
        nodeId: onWarpedHit({
          x: (event.clientX - rect.left - photoBox.left) * k,
          y: (event.clientY - rect.top - photoBox.top) * k,
        }),
        componentId: null,
      };
    }
    const ordered = [
      ...surfaces.filter((surface) => surface.component.id === activeComponentId),
      ...surfaces.filter((surface) => surface.component.id !== activeComponentId),
    ];
    for (const surface of ordered) {
      const hit = hitTestLayers(surface.layers, mmIn(viewOf(surface.component.id), event));
      // 그룹은 선택만 된다(스펙 §2-3). 자식은 그룹 로컬 좌표라 가장 바깥 그룹을 고른다.
      if (hit !== null)
        return { nodeId: hit.groupPath[0] ?? hit.nodeId, componentId: surface.component.id };
    }
    const under = ordered.find((surface) =>
      insideComponent(surface, mmIn(viewOf(surface.component.id), event)),
    );
    return { nodeId: null, componentId: under?.component.id ?? null };
  };

  const beginDrag = (
    kind: HandleKind,
    original: BoxNode,
    componentId: string,
    event: { clientX: number; clientY: number },
  ) => {
    setDrag({ kind, original, componentId, startMm: mmIn(viewOf(componentId), event) });
  };

  const capture = (pointerId: number) => {
    const host = hostRef.current;
    if (host !== null && typeof host.setPointerCapture === 'function')
      host.setPointerCapture(pointerId);
  };

  const release = (pointerId: number) => {
    const host = hostRef.current;
    if (
      host !== null &&
      typeof host.hasPointerCapture === 'function' &&
      host.hasPointerCapture(pointerId)
    )
      host.releasePointerCapture(pointerId);
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    capture(event.pointerId);
    if (selected !== null && selectedView !== null) {
      const kind = hitHandle(selected.node, mmIn(selectedView, event), {
        x: selectedView.pxPerMmX,
        y: selectedView.pxPerMmY,
      });
      if (kind !== null) {
        beginDrag(kind, selected.node, selected.componentId, event);
        return;
      }
    }
    const picked = pickAt(event);
    if (picked.nodeId === null) {
      // 빈 곳 — 영역 확대 모드면 끌어서 옮길 수 있으니 클릭 처리는 뗄 때(안 움직였을 때) 한다.
      if (fitMode === 'area') {
        setPan({
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          left: fit.left,
          top: fit.top,
          picked,
          moved: false,
        });
        return;
      }
      settleEmptyClick(picked);
      return;
    }
    if (picked.componentId !== null && picked.componentId !== activeComponentId)
      onActivate(picked.componentId);
    onSelect(picked.nodeId);
    const box = selectedBoxOf(surfaces, picked.nodeId);
    if (box !== null) beginDrag('move', box.node, box.componentId, event);
  };

  /** 빈 곳 클릭 — 다른 영역이면 그 영역으로 바꾸고, 선택은 푼다. */
  const settleEmptyClick = (picked: Picked) => {
    if (picked.componentId !== null && picked.componentId !== activeComponentId)
      onActivate(picked.componentId);
    onSelect(null);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (pan !== null) {
      const dx = event.clientX - pan.startX;
      const dy = event.clientY - pan.startY;
      const moved = pan.moved || Math.hypot(dx, dy) > PAN_THRESHOLD_PX;
      if (moved) {
        setFree({ k: fit.k, left: pan.left + dx, top: pan.top + dy });
        if (!pan.moved) setPan({ ...pan, moved: true });
      }
      return;
    }
    if (drag === null) return;
    const current = mmIn(viewOf(drag.componentId), event);
    const { kind, original, startMm } = drag;
    if (kind === 'move') setLive(dragMove(original, startMm, current));
    else if (kind === 'rotate') setLive(dragRotate(original, current, event.shiftKey));
    else if (kind === 'side-l' || kind === 'side-r') {
      if (original.type === 'text') setLive(dragTextWidth(original, kind, current));
    } else setLive(dragResize(original, kind, current));
  };

  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    release(event.pointerId);
    if (pan !== null) {
      if (!pan.moved) settleEmptyClick(pan.picked);
      setPan(null);
      return;
    }
    if (live !== null) onChangeNode(live);
    setLive(null);
    setDrag(null);
  };

  const onPointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    release(event.pointerId);
    setPan(null);
    setLive(null);
    setDrag(null);
  };

  const onDoubleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (onEditText === undefined) return;
    const picked = pickAt(event);
    if (picked.nodeId === null) return;
    const target = findNode(documentOf(surfaces), picked.nodeId);
    if (target?.node.type === 'text') onEditText(target.node.id);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && selectedNodeId !== null) {
      event.preventDefault();
      onSelect(null);
      return;
    }
    // 그룹은 상자가 없어 옮길 수 없지만 삭제는 된다(스펙 §2-3).
    if (selectedNodeId !== null && (event.key === 'Delete' || event.key === 'Backspace')) {
      event.preventDefault();
      onDeleteSelected();
      return;
    }
    if (selected === null) return;
    const step = event.shiftKey ? 10 : 1;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const move = moves[event.key];
    if (move !== undefined) {
      event.preventDefault();
      onNudge(move[0], move[1]);
    }
  };

  return (
    <div
      ref={hostRef}
      role="application"
      aria-label="디자인 편집 캔버스"
      data-testid="flat-canvas"
      data-mode="flat"
      data-active-component={activeComponentId}
      data-selected-x={selected?.node.x ?? ''}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: 키보드로 미세 이동·삭제하는 편집 표면이라 role=application 으로 포커스를 받는다.
      tabIndex={0}
      data-fit-mode={fitMode}
      className={cn(
        'relative size-full touch-none overflow-hidden select-none outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
        fitMode === 'area' && (pan?.moved ? 'cursor-grabbing' : 'cursor-grab'),
      )}
      onPointerDown={onPointerDown}
      onDoubleClick={onDoubleClick}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onKeyDown={onKeyDown}
    >
      {backdrop !== null && (
        <img
          src={backdrop.url}
          alt=""
          aria-hidden
          draggable={false}
          data-testid="flat-backdrop"
          className="pointer-events-none absolute max-w-none select-none"
          style={photoBox}
          onError={onBackdropError}
        />
      )}
      {warped !== null && backdrop !== null && (
        <canvas
          ref={warpedRef}
          aria-hidden
          data-testid="warped-preview"
          className={cn('pointer-events-none absolute', !warpedShown && 'invisible')}
          style={photoBox}
        />
      )}
      {surfaces.map((surface) => {
        const view = viewOf(surface.component.id);
        const isActive = surface.component.id === activeComponentId;
        const box = surfaceBoxOf(view, surface.component);
        // 모양 있는 영역은 사각형 흰 상자 대신 모양대로 흰 바탕을 캔버스 아래에 깐다(사진 없을 때만).
        const shaped = surface.component.shape !== undefined;
        return (
          <Fragment key={surface.component.id}>
            {backdrop === null && shaped && (
              <AreaShapeOutline
                component={surface.component}
                view={view}
                active={isActive}
                variant="fill"
              />
            )}
            <canvas
              ref={(element) => {
                if (element === null) canvasRefs.current.delete(surface.component.id);
                else canvasRefs.current.set(surface.component.id, element);
              }}
              data-testid={`flat-surface-${surface.component.id}`}
              data-active={isActive || undefined}
              onPointerEnter={() => setHoveredId(surface.component.id)}
              onPointerLeave={() => setHoveredId((id) => (id === surface.component.id ? null : id))}
              data-warped={warpedShown ? 'shown' : undefined}
              className={cn('absolute', backdrop === null && !shaped && 'bg-background shadow-sm')}
              style={box}
            />
            <AreaShapeOutline
              component={surface.component}
              view={view}
              active={isActive}
              emphasized={
                hoveredId === surface.component.id ||
                (selected !== null && selected.componentId === surface.component.id)
              }
            />
          </Fragment>
        );
      })}
      {guide !== null && selected !== null && selectedView !== null && (
        <canvas
          ref={guideRef}
          className="pointer-events-none absolute opacity-70"
          style={surfaceBoxOf(
            selectedView,
            surfaceOf(selected.componentId)?.component ?? { widthMm: 0, heightMm: 0 },
          )}
        />
      )}
      {selected !== null && selectedView !== null && (
        <SelectionOverlay
          node={selected.node}
          view={selectedView}
          onPointerDownHandle={(kind, event) => {
            event.stopPropagation();
            capture(event.pointerId);
            beginDrag(kind, selected.node, selected.componentId, event);
          }}
        />
      )}
      {selected !== null &&
        selectedView !== null &&
        selected.node.type === 'text' &&
        drag === null &&
        onEditText !== undefined && <TextEditHint node={selected.node} view={selectedView} />}
    </div>
  );
};
